import merge from 'lodash.merge';
import { BASE_THEME_SECTION, DESCOPE_PREFIX, PORTAL_THEME_PREFIX } from '../../constants';
import { isUrl, kebabCase } from '..';
import { getComponentName, getCssVarName } from '../componentHelpers';

const getVarName = (path) => getCssVarName(DESCOPE_PREFIX, ...path);

// lodash.set alternative
const set = (obj, path, value) => {
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

  pathArray.reduce((acc, key, i) => {
    if (acc[key] === undefined) acc[key] = {};
    if (i === pathArray.length - 1) acc[key] = value;
    return acc[key];
  }, obj);

  return obj;
};

const transformTheme = (theme, path, getTransformation) => {
  return Object.entries(theme).reduce((acc, [key, val]) => {
    if (val?.constructor !== Object) {
      return merge(acc, getTransformation(path.concat(key), val));
    }
    return merge(acc, transformTheme(val, [...path, key], getTransformation));
  }, {});
};

const stringifyArray = (strArr) =>
  strArr.map((str) => (str.includes(' ') ? `"${str}"` : str)).join(', ');

const getCssVarValue = (val) => {
  switch (true) {
    case Array.isArray(val):
      return stringifyArray(val);
    case isUrl(val):
      return `url(${val})`;
    default:
      return val;
  }
};

export const themeToCSSVarsObj = (theme) =>
  transformTheme(theme, [], (path, val) => ({
    [getVarName(path)]: getCssVarValue(val),
  }));

export const getThemeRefs = (theme, prefix) =>
  transformTheme(theme, [], (path) =>
    set({}, path, `var(${getVarName(prefix ? [prefix, ...path] : path)})`)
  );

export const getThemeVars = (theme, prefix) =>
  transformTheme(theme, [], (path) => set({}, path, getVarName(prefix ? [prefix, ...path] : path)));

export const globalsThemeToStyle = (theme, themeName = '') => {
  const style = Object.entries(themeToCSSVarsObj(theme)).reduce(
    (acc, entry) => `${acc}${entry.join(':')};\n`,
    ''
  );

  if (!themeName) return style;

  return `*[data-theme="${themeName}"] {${style}}`;
};

const componentsThemeToStyleObj = (componentsTheme) =>
  transformTheme(componentsTheme, [], (path, val) => {
    const [component, ...restPath] = path;
    const property = restPath.pop();
    const componentName = getComponentName(component);

    if (property === 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(componentName, `theme value: "${val}" is mapped to an invalid property`);
    }

    // we need a support for portal components theme (e.g. overlay)
    // this allows us to generate those themes under different sections
    // if the theme has root level attribute that starts with #
    // we are generating a new theme
    let themeName = BASE_THEME_SECTION;

    if (restPath[0] && restPath[0].startsWith(PORTAL_THEME_PREFIX)) {
      themeName = restPath.shift();
    }

    // do not start with underscore -> key:value, must have 2 no underscore attrs in a row
    // starts with underscore -> attribute selector
    const attrsSelector = restPath.reduce((acc, section, idx) => {
      if (section.startsWith('_')) return `${acc}[${kebabCase(section.replace(/^_/, ''))}="true"]`;

      const nextSection = restPath[idx + 1];

      if (typeof nextSection !== 'string' || nextSection.startsWith('_')) {
        // eslint-disable-next-line no-console
        console.error(
          'theme generator',
          `your theme structure is invalid, attribute "${section}" is followed by "${nextSection}" which is not allowed`
        );
        return acc;
      }

      return `${acc}[${kebabCase(section)}="${restPath.splice(idx + 1, 1).join('')}"]`;
    }, '');

    const selector = `:host${attrsSelector ? `(${attrsSelector})` : ''}`;

    return {
      [componentName]: {
        [themeName]: {
          [selector]: {
            [property]: getCssVarValue(val),
          },
        },
      },
    };
  });

const componentsThemeToStyle = (componentsTheme) =>
  Object.entries(componentsTheme).reduce(
    (acc, [selector, vars]) =>
      `${acc}${selector} { \n${Object.entries(vars)
        .map(([key, val]) => `${key}: ${val}`)
        .join(';\n')} \n}\n\n`,
    ''
  );

export const createComponentsTheme = (componentsTheme) => {
  const styleObj = componentsThemeToStyleObj(componentsTheme);

  return Object.keys(styleObj).reduce((acc, componentName) => {
    const componentThemes = styleObj[componentName];

    return Object.assign(acc, {
      [componentName]: Object.keys(componentThemes).reduce(
        (res, theme) =>
          Object.assign(res, { [theme]: componentsThemeToStyle(componentThemes[theme]) }),
        {}
      ),
    });
  }, {});
};

export const themeToStyle = ({ globals, components }, themeName) => ({
  globals: globalsThemeToStyle(globals, themeName),
  components: createComponentsTheme(components),
});

// allows to generate css variables with nested fallbacks
export const useVar = (...varNames) => {
  return varNames.reduceRight((acc, value) => {
    if (value.startsWith('--')) return `var(${value}${acc ? `, ${acc}` : acc})`;

    return `${value}${acc ? `, ${acc}` : acc}`;
  }, '');
};

export const createHelperVars = (theme, prefix) => {
  const res = transformTheme(theme, [], (path, value) => {
    const modifiedPath = [...path];
    const property = modifiedPath.splice(-1);
    const varName = getCssVarName(prefix, property);

    const vars = { [property]: varName };
    const useVars = { [property]: useVar(varName) };

    return { theme: set({}, [...modifiedPath, varName], value), useVars, vars };
  });

  return [res.theme, res.useVars, res.vars];
};

export { componentsThemeManager } from '@descope-ui/common/theme-helpers';
